/**
 * Release Material Modal
 * Releases materials against a pending / partially-released MRN and records the Inventory Consumption.
 * Select a warehouse (shows available stock per item) and a cost center (the consumer), then
 * enter the actual released quantity per line. Released quantities are issued out of the
 * warehouse's stock, so a line cannot release more than is available there.
 */

import React, { useState, useEffect, useCallback } from 'react';
import SearchableSelect from '../../Common/SearchableSelect';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';
import { useLocation } from '../../../contexts/LocationContext';
import toast from 'react-hot-toast';
import useClosedPeriod from '../../../hooks/useClosedPeriod';

const WAREHOUSE_TYPES = ['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'];

const ReleaseMaterialModal = ({ mrn, onClose, onSuccess }) => {
  const { currentLocation } = useLocation();
  const currentLocationId = currentLocation?._id || currentLocation?.id;

  const [warehouse, setWarehouse] = useState(mrn?.requestWarehouse || '');
  const [warehouseName, setWarehouseName] = useState(mrn?.requestWarehouseName || '');
  const [costCenter, setCostCenter] = useState(mrn?.costCenter?._id || mrn?.costCenter || '');
  const [costCenterName, setCostCenterName] = useState(mrn?.costCenterName || '');
  const [consumptionDate, setConsumptionDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');

  const [warehouses, setWarehouses] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [loadingStock, setLoadingStock] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Editable released quantities keyed by MRN line _id.
  const [releaseLines, setReleaseLines] = useState(() =>
    (mrn?.lineItems || []).map(li => ({
      mrnLineItemId: li._id,
      item: li.item?._id || li.item,
      itemCode: li.itemCode,
      itemDescription: li.itemDescription,
      uom: li.uom,
      quantityRequested: li.quantityRequested || 0,
      quantityAlreadyReleased: li.quantityReleased || 0,
      quantityReleased: '',
      // Expense account: the item's own (itemExpense) or a ledger chosen here for items without one
      itemExpense: undefined, // undefined = loading, null = item has none
      ledgerId: '',
      ledgerLabel: '',
      saveToItem: true
    }))
  );

  // Which lines already have an expense account on their item
  const { minDate, dateError } = useClosedPeriod();
  const closedError = dateError(consumptionDate);

  useEffect(() => {
    if (!mrn?._id) return;
    inventoryApi.getMaterialRequestNoteExpenseAccounts(mrn._id)
      .then(res => {
        const byLine = new Map((res.data || []).map(r => [String(r.mrnLineItemId), r.expenseAccount]));
        setReleaseLines(prev => prev.map(l => ({ ...l, itemExpense: byLine.has(String(l.mrnLineItemId)) ? byLine.get(String(l.mrnLineItemId)) : null })));
      })
      .catch(() => setReleaseLines(prev => prev.map(l => ({ ...l, itemExpense: null }))));
  }, [mrn?._id]);

  const updateLine = (index, patch) => {
    setReleaseLines(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  useEffect(() => {
    // Scope warehouses to the logged-in location only.
    if (!currentLocationId) {
      setWarehouses([]);
      return;
    }
    adminApi.getLocations({ type: 'warehouse', parentId: currentLocationId, limit: 1000 })
      .then(res => {
        const locs = res.data?.data || res.data || [];
        setWarehouses((Array.isArray(locs) ? locs : []).filter(l => WAREHOUSE_TYPES.includes(l.type)));
      })
      .catch(() => setWarehouses([]));
  }, [currentLocationId]);

  const loadStock = useCallback(async (warehouseId) => {
    if (!warehouseId) {
      setStockMap({});
      return;
    }
    try {
      setLoadingStock(true);
      const itemIds = releaseLines.map(l => l.item).filter(Boolean);
      const response = await inventoryApi.getStockAvailabilityByWarehouse(warehouseId, itemIds);
      if (response.success) {
        const map = {};
        (response.data || []).forEach(stock => {
          map[stock.item._id] = stock.stock?.available ?? stock.quantity ?? 0;
        });
        setStockMap(map);
      }
    } catch (err) {
      console.error('Error loading stock availability:', err);
      setStockMap({});
    } finally {
      setLoadingStock(false);
    }
  }, [releaseLines]);

  useEffect(() => {
    loadStock(warehouse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouse]);

  const warehouseOptions = warehouses.map(w => ({ value: w._id, label: w.name }));

  const handleWarehouseChange = (e) => {
    const value = e.target.value;
    const wh = warehouses.find(w => w._id === value);
    setWarehouse(value);
    setWarehouseName(wh ? wh.name : '');
    setErrors(prev => ({ ...prev, warehouse: '' }));
  };

  const handleReleasedChange = (index, value) => {
    setReleaseLines(prev => {
      const next = [...prev];
      next[index] = { ...next[index], quantityReleased: value };
      return next;
    });
    setErrors(prev => ({ ...prev, [`line_${index}`]: '' }));
  };

  const remainingFor = (line) => (line.quantityRequested || 0) - (line.quantityAlreadyReleased || 0);

  const validate = () => {
    const newErrors = {};
    if (!warehouse) newErrors.warehouse = 'Select a warehouse';
    if (!costCenter) newErrors.costCenter = 'Select a cost center';

    let anyReleased = false;
    releaseLines.forEach((line, index) => {
      const qty = parseFloat(line.quantityReleased);
      if (!line.quantityReleased) return;
      if (qty <= 0) {
        newErrors[`line_${index}`] = 'Must be greater than 0';
        return;
      }
      const remaining = remainingFor(line);
      if (qty > remaining + 1e-9) {
        newErrors[`line_${index}`] = `Max ${remaining}`;
        return;
      }
      // Released materials leave the warehouse's stock
      const available = stockMap[line.item] != null ? stockMap[line.item] : 0;
      if (warehouse && qty > available + 1e-9) {
        newErrors[`line_${index}`] = `Only ${available} in stock`;
        return;
      }
      // Charged to the item's expense account; items without one need a ledger for this line
      if (!line.itemExpense?.accountId && !line.ledgerId) {
        newErrors[`ledger_${index}`] = 'Select an expense ledger';
      }
      anyReleased = true;
    });

    if (!anyReleased) newErrors.form = 'Enter a released quantity for at least one item';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        warehouse,
        warehouseName,
        costCenter,
        costCenterName,
        consumptionDate,
        notes,
        lineItems: releaseLines
          .filter(l => parseFloat(l.quantityReleased) > 0)
          .map(l => ({
            mrnLineItemId: l.mrnLineItemId,
            quantityReleased: parseFloat(l.quantityReleased),
            availableStock: stockMap[l.item] != null ? stockMap[l.item] : 0,
            ...(!l.itemExpense?.accountId && l.ledgerId && {
              expenseLedgerId: l.ledgerId,
              expenseLedgerLabel: l.ledgerLabel,
              saveToItem: !!l.saveToItem
            })
          }))
      };

      await inventoryApi.releaseMaterialRequestNote(mrn._id, payload);
      toast.success('Materials released — stock issued and consumption recorded');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to release materials');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (hasError) =>
    `w-full text-xs px-2 py-1.5 border rounded focus:outline-none focus:ring-1 ${
      hasError ? 'border-red-300 bg-red-50 focus:ring-red-400' : 'border-gray-300 focus:ring-indigo-500'
    }`;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">Release Materials — {mrn.mrnNo}</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-4 py-3">
            {/* Header fields */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Warehouse <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  value={warehouse}
                  onChange={handleWarehouseChange}
                  options={warehouseOptions}
                  placeholder="Select warehouse"
                  error={errors.warehouse || ''}
                  size="sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Cost Center (Consumer) <span className="text-red-500">*</span>
                </label>
                <SearchableCostCenterDropdown
                  value={costCenter}
                  displayLabel={costCenterName}
                  onChange={(id, label) => {
                    setCostCenter(id);
                    setCostCenterName(label || '');
                    setErrors(prev => ({ ...prev, costCenter: '' }));
                  }}
                  error={!!errors.costCenter}
                  menuPlacement="down"
                />
                {errors.costCenter && <p className="mt-1 text-xs text-red-600">{errors.costCenter}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Consumption Date</label>
                <input
                  type="date"
                  value={consumptionDate}
                  min={minDate}
                  onChange={(e) => setConsumptionDate(e.target.value)}
                  className={inputCls(false)}
                />
                {closedError && <p className="text-xs text-red-600 mt-0.5">{closedError}</p>}
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-2">
              Each line is charged to its item's expense account (Dr expense / Cr Inventory at stock cost, for the cost center above).
              Items without one need an expense ledger on the line.
            </p>
            {errors.form && <p className="text-xs text-red-600 mb-1">{errors.form}</p>}

            {/* Line items */}
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Item</th>
                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">UoM</th>
                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Requested</th>
                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Already Released</th>
                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Remaining</th>
                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Available Stock</th>
                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Release Now</th>
                    <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 min-w-[240px]">Expense Account</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {releaseLines.map((line, index) => {
                    const remaining = remainingFor(line);
                    const available = warehouse ? stockMap[line.item] : null;
                    return (
                      <tr key={line.mrnLineItemId || index}>
                        <td className="px-2 py-1.5">
                          <div className="text-xs font-medium text-gray-900">{line.itemDescription}</div>
                          <div className="text-xs text-gray-500 font-mono">{line.itemCode}</div>
                        </td>
                        <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.uom || '—'}</td>
                        <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityRequested}</td>
                        <td className="px-2 py-1.5 text-center text-xs text-gray-600">{line.quantityAlreadyReleased || 0}</td>
                        <td className="px-2 py-1.5 text-center text-xs font-medium text-gray-900">{remaining}</td>
                        <td className="px-2 py-1.5 text-center text-xs">
                          {!warehouse ? (
                            <span className="text-gray-400">—</span>
                          ) : loadingStock ? (
                            <span className="text-gray-400 animate-pulse">…</span>
                          ) : available == null ? (
                            <span className="text-red-500">No stock</span>
                          ) : (
                            <span className={`font-medium ${available === 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {available}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            max={remaining}
                            disabled={remaining <= 0}
                            value={line.quantityReleased}
                            onChange={(e) => handleReleasedChange(index, e.target.value)}
                            className={`${inputCls(!!errors[`line_${index}`])} text-center w-28`}
                            placeholder={remaining <= 0 ? 'Done' : '0'}
                          />
                          {errors[`line_${index}`] && (
                            <p className="mt-0.5 text-[10px] text-red-600 text-center">{errors[`line_${index}`]}</p>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          {line.itemExpense === undefined ? (
                            <span className="text-xs text-gray-400 animate-pulse">…</span>
                          ) : line.itemExpense?.accountId ? (
                            <div className="text-xs">
                              <span className="text-gray-800">{line.itemExpense.label || 'Item expense account'}</span>
                              <span className="ml-1 text-[10px] text-green-700">(item)</span>
                            </div>
                          ) : (
                            <div className={parseFloat(line.quantityReleased) > 0 ? '' : 'opacity-60'}>
                              <SearchableLedgerDropdown
                                value={line.ledgerId}
                                displayLabel={line.ledgerLabel}
                                onChange={(id, label) => { updateLine(index, { ledgerId: id || '', ledgerLabel: label || '' }); setErrors(prev => ({ ...prev, [`ledger_${index}`]: '' })); }}
                                placeholder="No item account — select ledger"
                                error={!!errors[`ledger_${index}`]}
                                menuPlacement="auto"
                              />
                              <label className="mt-1 flex items-center gap-1 text-[10px] text-gray-500">
                                <input type="checkbox" checked={line.saveToItem} onChange={(e) => updateLine(index, { saveToItem: e.target.checked })} />
                                Save to the item for next time
                              </label>
                              {errors[`ledger_${index}`] && <p className="mt-0.5 text-[10px] text-red-600">{errors[`ledger_${index}`]}</p>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputCls(false)}
                placeholder="Optional notes"
              />
            </div>

            {/* Actions */}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !!closedError}
                className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded transition-all disabled:opacity-50"
              >
                {loading ? 'Recording…' : 'Release & Record Consumption'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReleaseMaterialModal;
